import { LogManager } from '@src/utility/Logger';

import type { Result } from '@src/services/common/Result';

/**
 * MediaCacheService
 * Stateless helper to fetch media via Cache Storage with a canonical cache key
 * (origin + pathname) so signed URL query parameters don't bust the cache.
 * Performs optional background refresh (SWR) on cache hits.
 */
export class MediaCacheService {
  private logger = LogManager.getLogger('service:MediaCacheService');

  /**
   * Build a canonical cache key that ignores query/signature.
   * Optionally append a stable variant suffix if required to disambiguate.
   */
  private buildCacheKey(url: string, variant?: string): string {
    try {
      const u = new URL(url);
      const base = `${u.origin}${u.pathname}`;
      return variant ? `${base}#${variant}` : base;
    } catch {
      const base = url.split('?')[0];
      return variant ? `${base}#${variant}` : base;
    }
  }

  /**
   * Fetch media as a Blob, using Cache Storage when available.
   * - On cache hit: returns cached response and refreshes cache in background.
   * - On miss: fetches from network and stores under canonical key.
   */
  async fetchMediaBlob(
    sourceUrl: string,
    options?: { variantKey?: string },
  ): Promise<Result<{ blob: Blob; fromCache: boolean }>> {
    const variantKey = options?.variantKey;
    const cacheName = 'dato-video-plugin-media-v1';

    try {
      let resp: Response | undefined;
      let fromCache = false;

      if (typeof caches !== 'undefined') {
        const cache = await caches.open(cacheName);
        const cacheKey = this.buildCacheKey(sourceUrl, variantKey);

        // Try cache first
        resp = await cache.match(cacheKey);
        if (resp) {
          fromCache = true;
          // Background refresh to keep cache up-to-date
          fetch(sourceUrl, { cache: 'reload' })
            .then(async r => {
              if (r && (r.ok || r.type === 'opaque')) {
                try {
                  await cache.put(cacheKey, r.clone());
                } catch (e) {
                  this.logger.warn('SWR cache.put failed', { error: e });
                }
              }
            })
            .catch(() => {});
        } else {
          // Network fetch with reload to bypass HTTP cache layers so we control via CacheStorage
          const net = await fetch(sourceUrl, { cache: 'reload' });
          try {
            if (net && (net.ok || net.type === 'opaque')) {
              await cache.put(cacheKey, net.clone());
            }
          } catch (e) {
            this.logger.warn('Cache.put failed', { error: e });
          }
          resp = net;
        }
      }

      // Fallback if CacheStorage unavailable or earlier branch didn't set resp
      if (!resp) {
        resp = await fetch(sourceUrl);
      }

      const blob = await resp.blob();
      return { ok: true, value: { blob, fromCache } };
    } catch (error) {
      this.logger.error('fetchMediaBlob failed', { error, sourceUrl });
      return {
        ok: false,
        error: {
          code: 'EXTERNAL',
          message: 'Failed to fetch media',
          details: error,
        },
      };
    }
  }
}

export default MediaCacheService;
